import Swal from "sweetalert2";
import styled from 'styled-components';
import EditIcon from 'mdi-react/EditIcon';
import { Spinner } from 'react-bootstrap';
import DeleteIcon from 'mdi-react/DeleteIcon';
import { MissionProviderRoleEnum } from "Enums";
import React, { useState, useEffect } from 'react';
import AdminInterface from '@/utils/AdminInterface';
import withReactContent from "sweetalert2-react-content";
import { paddingLeft, paddingRight } from '@/utils/directions';
import ReactTableBase from '@/shared/components/table/ReactTableBase';
import { FormContainer } from '@/shared/components/form/FormElements';
import { SelectField } from "@/containers/shared/components/form/Select";
import { colorAdditional, colorGreenHover, colorAccent } from '@/utils/palette';
import { Card, CardBody, CardTitleWrap, CardTitle, CardSubhead } from '@/shared/components/Card';

const CartTableButton = styled.button`
  background: transparent;
  border: none;
  line-height: 14px;
  ${paddingLeft}: 20px;
  ${paddingRight}: 0;
  padding-top: 0;
  padding-bottom: 0;
  position: relative;
  color: ${colorAdditional};
  cursor: pointer;
  transition: all 0.3s;

  svg {
    height: 20px;
    width: 20px;
    fill: ${colorAdditional};
    transition: all 0.3s;
  }

  &:hover {
    color: ${colorGreenHover};

    svg {
      fill: ${colorGreenHover};
    }
  }
`;

const TableSpinner = styled(Spinner)`
  color: ${colorAccent};
`;

const MissionDetailDiv = styled.div`
    color: ${colorAdditional};
    
    li::marker {
        color: ${colorAdditional};
    }

    ul {
        list-style-image: none;
        list-style-type: disc;
    }

    li {
        color: ${colorAdditional};
    }
`;

const SearchWrap = styled(FormContainer)`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  margin-bottom: 10px;
  
  span {
    width: 150px;
    color: white;
  }

  input {
    margin-right: 10px;
  }

  .react-select__menu-list {
    background: white;
    overflow: auto;
  }
`;

const AssetsList = (props) => {
    const [showLoader, setShowLoader] = useState(false);
    const [assetsList, setAssetsList] = useState({ tableHeaderData: [], tableRowsData: [] });
    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 20 })
    const [pageCount, setPageCount] = useState(1);
    const [missions, setMissions] = useState();
    const [selectedMission, setSelectedMission] = useState();
    const [totalAssetsCount, setTotalAssetsCount] = useState();
    const [missionAssetsCount, setMissionAssetsCount] = useState(pagination.pageSize);

    const tableConfig = {
        isEditable: false,
        isSortable: false,
        isResizable: false,
        withPagination: true,
        withSearchEngine: false,
        manualPageSize: [pagination.pageSize],
    };

    useEffect(() => {
        GetAssets(pagination.pageIndex * pagination.pageSize, pagination.pageSize);
    }, [pagination]);

    const handleEdit = (asset) => {
        props.setSelectedAsset(asset);
    }

    const handleDelete = (asset) => {
        Swal.fire({
            title: 'Warning!',
            text: `Do you want to delete the asset ${asset.layer_name}? You will not be able to recover this asset.`,
            icon: 'warning',
            confirmButtonText: 'Yes!',
            cancelButtonText: 'No!',
            showCancelButton: true
        }).then((result) => {
            if (result.isConfirmed) {
                AdminInterface.instance.deleteAsset(asset.uuid).then(() => {
                    withReactContent(Swal).fire({ title: "Success", text: "Asset is deleted!" });
                    props.setListRequiresReload(true);
                }).catch((error) => {
                    withReactContent(Swal).fire({ title: "Error", html: error.response?.data?.detail });
                });
            }
        });
    };

    const GetAssets = (skip, limit) => {
        if (selectedMission === undefined) return;
        setShowLoader(true);
        AdminInterface.instance.getAssetsInMission(selectedMission.id, skip, limit).then((response) => {
            const columns = [
                {
                    accessor: 'id',
                    Header: 'Identifer',
                },
                {
                    accessor: 'start_datetime',
                    Header: 'Start Datetime',
                },
                {
                    accessor: 'no_of_bands',
                    Header: 'No. Bands',
                },
                {
                    accessor: 'main_file_uri',
                    Header: 'File URI',
                    disableGlobalFilter: true,
                    disableSortBy: true,
                },
                {
                    accessor: 'cog_file_uri',
                    Header: 'COG URI',
                    disableGlobalFilter: true,
                    disableSortBy: true,
                },
                {
                    accessor: 'delete',
                    Header: 'Delete',
                    disableGlobalFilter: true,
                    disableSortBy: true,
                },
                {
                    accessor: 'edit',
                    Header: 'Edit',
                    disableGlobalFilter: true,
                    disableSortBy: true,
                }
            ];
            const data = [];
            response.data.forEach(asset => {
                data.push({
                    id: asset.uuid,
                    start_datetime: new Date(`${asset.start_datetime}z`).toLocaleString(),
                    no_of_bands: asset.no_of_bands,
                    providers: asset.item_json['providers'].length,
                    licensor: asset.item_json['properties']['license_provider'],
                    license: <a href={asset.item_json['properties']['license_link']} target="_blank">{asset.item_json['properties']['license']}</a>,
                    instruments: asset.item_json['properties']['instruments'].length != 0 ? asset.item_json['properties']['instruments'].join(', ') : '',
                    main_file_uri: asset.main_file_uri,
                    cog_file_uri: asset.cog_file_uri,
                    edit: <CartTableButton type="button" onClick={() => handleEdit(asset)}><EditIcon /></CartTableButton>,
                    delete: <CartTableButton type="button" onClick={() => handleDelete(asset)}><DeleteIcon /></CartTableButton>,
                });
            });

            setAssetsList({ tableHeaderData: columns, tableRowsData: data });
        }).catch((error) => {
            withReactContent(Swal).fire({ title: "Error", text: "Error in loading assets." });
        }).finally(() => {
            setShowLoader(false);
            props.setListRequiresReload(false);
        });
    }

    useEffect(() => {
        if (props.listRequiresReload) GetAssets(pagination.pageIndex * pagination.pageSize, pagination.pageSize);
    }, [props.listRequiresReload]);

    useEffect(() => {
        if (selectedMission === undefined) return;
        props.setSelectedAsset(undefined);
        AdminInterface.instance.getNumberOfAssetsInMission(selectedMission.id).then((response) => {
            setMissionAssetsCount(response.data['count']);
            setPageCount(Math.ceil(response.data['count'] / pagination.pageSize));
            setPagination({ pageIndex: 0, pageSize: 20 })
        }).catch(() => {
            withReactContent(Swal).fire({ title: "Error", html: 'Error in getting the total number of assets.' });
        });
    }, [selectedMission]);

    const SetMission = (selectedMission) => {
        setSelectedMission(selectedMission.value);
    }

    const GetMissions = () => {
        AdminInterface.instance.getMissions().then((response) => {
            let missionsList = response.data.map(function (mission) {
                return { value: mission, label: `${mission.collection.name} ${mission.name}` }
            });

            setMissions(missionsList);
        }).catch(() => {
            withReactContent(Swal).fire({
                icon: 'error',
                title: 'Error',
                html: 'Error in getting missions.'
            });
        });
    }

    const GetAssetsCount = () => {
        AdminInterface.instance.getNumberOfAssets().then((response) => {
            setTotalAssetsCount(response.data['count']);
        }).catch(() => {
            withReactContent(Swal).fire({
                icon: 'error',
                title: 'Error',
                html: 'Error in getting the number of assets.'
            });
        });
    }

    useEffect(() => {
        GetMissions();
        GetAssetsCount();
    }, []);

    return (
        <Card>
            <CardBody>
                <CardTitleWrap>
                    <CardTitle>Assets List</CardTitle>
                    <CardSubhead>Total Count: {totalAssetsCount}</CardSubhead>
                </CardTitleWrap>
                <SearchWrap>
                    <span>Select Mission</span>
                    {missions && <SelectField options={missions} onChange={SetMission} text='Test' />}
                </SearchWrap>
                {showLoader && (<div className="text-center"><TableSpinner animation="border" /></div>)}
                {selectedMission && <MissionDetailDiv>
                    <span style={{ fontWeight: 'bold', display: 'inline-block', minWidth: 130 }}>Mission Details</span>
                    <ul>
                        <li><span style={{ fontWeight: 'bold', display: 'inline-block', minWidth: 130 }}>Collection:</span><span style={{ marginLeft: 8 }}>{selectedMission.collection.name}</span></li>
                        <li><span style={{ fontWeight: 'bold', display: 'inline-block', minWidth: 130 }}>Mission:</span><span style={{ marginLeft: 8 }}>{selectedMission.name}</span></li>
                        <li><span style={{ fontWeight: 'bold', display: 'inline-block', minWidth: 130 }}>Start Datetime:</span><span style={{ marginLeft: 8 }}>{new Date(`${selectedMission.start_datetime}z`).toLocaleString()}</span></li>
                        <li><span style={{ fontWeight: 'bold', display: 'inline-block', minWidth: 130 }}>End Datetime:</span><span style={{ marginLeft: 8 }}>{new Date(`${selectedMission.end_datetime}z`).toLocaleString()}</span></li>
                        <li><span style={{ fontWeight: 'bold', display: 'inline-block', minWidth: 130 }}>Instrument:</span><span style={{ marginLeft: 8 }}>{selectedMission.instruments.map(instrument => instrument.instrument.name).join(', ')}</span></li>
                        <li><span style={{ fontWeight: 'bold', display: 'inline-block', minWidth: 130 }}>Producer:</span><span style={{ marginLeft: 8 }}>{selectedMission.providers.filter(provider => provider.role === MissionProviderRoleEnum.producer).map(provider => provider.provider.name).join(', ')}</span></li>
                        <li><span style={{ fontWeight: 'bold', display: 'inline-block', minWidth: 130 }}>Processor:</span><span style={{ marginLeft: 8 }}>{selectedMission.providers.filter(provider => provider.role === MissionProviderRoleEnum.processor).map(provider => provider.provider.name).join(', ')}</span></li>
                        <li><span style={{ fontWeight: 'bold', display: 'inline-block', minWidth: 130 }}>Host:</span><span style={{ marginLeft: 8 }}>{selectedMission.providers.filter(provider => provider.role === MissionProviderRoleEnum.host).map(provider => provider.provider.name).join(', ')}</span></li>
                        <li><span style={{ fontWeight: 'bold', display: 'inline-block', minWidth: 130 }}>Licensor:</span><span style={{ marginLeft: 8 }}>{selectedMission.providers.filter(provider => provider.role === MissionProviderRoleEnum.licensor).map(provider => provider.provider.name).join(', ')}</span></li>
                        <li><span style={{ fontWeight: 'bold', display: 'inline-block', minWidth: 130 }}>License:</span><span style={{ marginLeft: 8 }}><a href={selectedMission.providers.filter(provider => provider.role === MissionProviderRoleEnum.licensor).map(provider => provider.license.link)} target="_blank">{selectedMission.providers.filter(provider => provider.role === MissionProviderRoleEnum.licensor).map(provider => provider.license.name).join(', ')}</a></span></li>
                        <li><span style={{ fontWeight: 'bold', display: 'inline-block', minWidth: 130 }}>Country:</span><span style={{ marginLeft: 8 }}>{selectedMission.country}</span></li>
                        <li><span style={{ fontWeight: 'bold', display: 'inline-block', minWidth: 130 }}>State/Province:</span><span style={{ marginLeft: 8 }}>{selectedMission.state}</span></li>
                        <li><span style={{ fontWeight: 'bold', display: 'inline-block', minWidth: 130 }}>City:</span><span style={{ marginLeft: 8 }}>{selectedMission.city}</span></li>
                        <li><span style={{ fontWeight: 'bold', display: 'inline-block', minWidth: 130 }}>Total Assets:</span><span style={{ marginLeft: 8 }}>{missionAssetsCount}</span></li>
                    </ul>
                </MissionDetailDiv>}
                {<>
                    <ReactTableBase columns={assetsList.tableHeaderData} data={assetsList.tableRowsData} tableConfig={tableConfig} setPagination={setPagination} pageCount={pageCount} dataLength={missionAssetsCount} />
                </>}
            </CardBody>
        </Card>
    );
};

export default AssetsList;